import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Sidebar } from '../Home/Sidebar';
import { SIDEBAR_SECTIONS } from '../Home/sidebarSections';
import { APP_LINK_STATE, NAV_ROUTES } from '../Home/navRoutes';
import { useAuth } from '../../core/authContext';
import { FEATURE_LANGUAGE_OVER_TIME } from '../../core/features';
import { findInRoster, loadPlanRoster } from '../Upload/studentRoster';
import { hydrateRostersFromUploads } from '../Data/uploadedFiles';
import { AltHeader, buildAltHeader } from './AltHeader';
import { AltPlanSnapshot } from './AltPlanSnapshot';
import { AltFocusByDomain } from './AltFocusByDomain';
import { AltProficiencyPanel } from './AltProficiencyPanel';
import { AltTrajectory } from './AltTrajectory';
import { AltDomainProfile } from './AltDomainProfile';
import { accessYearsOnly, altAccess, areasForDevelopment, capabilities, type PlanCriteria, type PlanStudent } from './derive';
import { usePlansStatus } from '../Plans/plansGenerated';
import { sortByPlanName } from '../Plans/planRows';
import { findSisStudent } from '../Upload/sisRoster';
import { fetchStudentPlanData, hasLiveValue, type StudentPlanData } from './planData';
import { fetchFlashlightBenchmarks, type FlashlightBenchmarksResponse } from './flashlightBenchmarks';
import { getFlashlightData } from './flashlight';
import { FlashlightBenchmarksCard, type PlanBenchmarksSource } from './FlashlightBenchmarksCard';
import { calendarSchoolYearEnd } from '../../components/flashlightGrid';
import { TopBar, type Crumb, type RosterEntry } from './TopBar';
import { PlanHeader, YearMenu, YearPill, type PlanYearMenu } from './PlanHeader';
import { MetaStrip } from './MetaStrip';
import { PlanSnapshot, type FrozenSnapshot } from './PlanSnapshot';
import { FocusByDomain } from './FocusByDomain';
import { ProficiencyCard } from './ProficiencyCard';
import { CompositeTrajectory } from './CompositeTrajectory';
import { DomainProfile } from './DomainProfile';
import { StateAssessments } from './StateAssessments';
import { PlanCard, PlanChip } from './components';
import { COLORS, PLAN_INFO_BAR } from './tokens';
import { elTeacherName, gradeText, metaItems, schoolName, sisFieldsMissing } from './headerData';
import { enrichWithSis } from './sisEnrich';
import { ReclassCriteriaProvider, criteriaForStudent, useDistrictExitCriteria } from './reclassCriteriaContext';
import type { FocusByDomainContent, FocusByDomainInputs } from './domainFocus';
import type { AccommodationInputs } from './accommodations';
import { appendPlanSnapshotScores, type PlanSnapshotContent, type PlanSnapshotInputs } from './planSnapshotContent';
import { buildPlanSnapshot, saveStudentPlan, type PlanGenerationInputs } from './studentPlan';
import { resetPlanCache } from './planCache';
import { useDistrictYears } from '../../core/useDistrictYears';
import { schoolYearEndYear } from '../../core/schoolYear';
import { planPath } from './planRoute';
import { PAGE_MAX_WIDTH } from '../../theme';
import {
  addPlanNote,
  fetchPlanRecord,
  finalizePlan,
  revisePlan,
  savePlanAccommodations,
  savePlanSnapshot,
  signOffPlan,
  PlanRecordConflictError,
  PlanRecordForbiddenError,
  PlanRecordNotFoundError,
  type AccommodationInput,
  type PlanAccommodation,
  type PlanRecord,
  type PlanStanding,
  type SnapshotSaveInput,
} from './planRecordApi';
import { AccommodationsCard } from './AccommodationsCard';
import { NotesCard } from './NotesCard';
import { DraftBanner, SignOffBar } from './SignOffBar';
import { SignOffsCard } from './SignOffsCard';
import { FinalizeDialog, FinalizedToast, finalizedToastMessage, nextStudentAfter, type OrderEntry } from './FinalizeDialog';
import { displayYear, formatPlanDate, formatPlanMonthDay, previousYear } from './planRecordView';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

type PlansBackState = { count?: number; order?: string[] } | null;

type SavedBody = {
  fullName?: string;
  student?: PlanStudent | null;
  planSnapshot?: PlanSnapshotContent | null;
  accommodations?: string[] | null;
  focusByDomain?: Record<string, FocusByDomainContent> | null;
  criteria?: PlanCriteria | null;
};

function fullDate(d: Date): string {
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
}

function shortDate(d: Date): string {
  return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(2)}`;
}

function PageShell({ topBar, children }: { topBar: React.ReactNode; children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', minHeight: HEIGHT_BELOW_BARS, bgcolor: COLORS.bg }}>
      <Sidebar sections={SIDEBAR_SECTIONS} activeId="roster" />
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {topBar}
        <Box sx={{ flex: 1, px: { xs: 3, md: 5 }, py: { xs: 3, md: 4 } }}>
          <Box sx={{ maxWidth: PAGE_MAX_WIDTH, mx: 'auto' }}>{children}</Box>
        </Box>
      </Box>
    </Box>
  );
}

function InfoBanner({ message, actionLabel, onAction }: { message: string; actionLabel: string; onAction: () => void }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, px: 2, py: 1, mb: 2, borderRadius: 2, bgcolor: PLAN_INFO_BAR.bg, border: `1px solid ${PLAN_INFO_BAR.border}` }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <InfoOutlinedIcon sx={{ color: PLAN_INFO_BAR.icon }} />
        <Typography sx={{ fontSize: 16, fontWeight: 500, lineHeight: '24px', color: PLAN_INFO_BAR.fg }}>{message}</Typography>
      </Box>
      <Button onClick={onAction} sx={{ textTransform: 'none', fontWeight: 600, fontSize: 16, color: COLORS.navy, flexShrink: 0 }}>
        {actionLabel}
      </Button>
    </Box>
  );
}

function pastYearMessage(firstName: string, year: string, finalized: boolean): string {
  return finalized
    ? `You're viewing ${firstName}'s ${displayYear(year)} plan as it was finalized. Past plans can't be changed.`
    : `You're viewing ${firstName}'s ${displayYear(year)} plan as it was last generated. It was never finalized, and past plans can't be changed.`;
}

function pastPlanMessage(firstName: string, year: string, standing: PlanStanding, finalized: boolean): string {
  const shown = `You're viewing ${firstName}'s ${displayYear(year)} plan as it was ${finalized ? 'finalized' : 'generated'}`;
  if (standing === 'departed') return `${shown}. Past plans can't be changed.`;
  if (standing === 'unknown') return `${shown}. The plan can't be changed, but you can still add notes.`;
  return `${shown}, while ${firstName} was an active EL. The plan can't be changed, but you can still add notes.`;
}

function notesSubtitle(firstName: string, districtName: string, standing: PlanStanding): string | undefined {
  if (standing === 'active') return undefined;
  if (standing === 'departed') return `${firstName} has left ${districtName}, so notes can be read but not added.`;
  if (standing === 'unknown') return `Notes stay with ${firstName} from year to year.`;
  if (standing === 'waived') return `Notes stay with ${firstName} from year to year, including while ${firstName}'s EL services are waived.`;
  return `Notes stay with ${firstName} from year to year, including while ${firstName} is ${standing === 'monitoring' ? 'monitored' : 'a former EL'}.`;
}

function throughPlanYear(student: PlanStudent, planYearEnd: number): PlanStudent {
  const years = student.years.filter((y) => y.year <= planYearEnd);
  const last = years[years.length - 1];
  return { ...student, years, yearsInEl: last?.yearsInProgram ?? null, latestGrade: last?.grade ?? '', latestSchool: last?.school ?? '', sis: null, tenure: undefined };
}

function olderThanShown(next: PlanRecord, current: PlanRecord | null): boolean {
  if (current == null || next.year !== current.year) return false;
  if (next.version.number !== current.version.number) return next.version.number < current.version.number;
  return next.version.revision < current.version.revision;
}

function bodyOf(record: PlanRecord | null): SavedBody | null {
  const body = record?.content?.body;
  return body && typeof body === 'object' ? (body as SavedBody) : null;
}

function frozenSnapshotOf(body: SavedBody | null, student: PlanStudent | null, criteria: PlanCriteria): FrozenSnapshot | null {
  if (!body) return null;
  const content = body.planSnapshot;
  const scored = content && student && content.capabilities?.length === 4 ? appendPlanSnapshotScores(content, student, criteria) : content;
  const fallback = student ? { capabilities: capabilities(student, criteria), areasForDevelopment: areasForDevelopment(student, criteria) } : { capabilities: [], areasForDevelopment: [] };
  return {
    capabilities: scored?.capabilities ?? fallback.capabilities,
    areasForDevelopment: scored?.areasForDevelopment ?? fallback.areasForDevelopment,
    supports: Array.isArray(body.accommodations) ? body.accommodations : [],
    raw: content ? { capabilities: content.capabilities, areasForDevelopment: content.areasForDevelopment } : fallback,
  };
}

function suggestedAsAccommodations(record: PlanRecord, ids: string[]): PlanAccommodation[] {
  if (record.accommodationsSaved || record.alternateAccess || !record.catalog) return record.accommodations;
  const present = new Set(record.accommodations.map((a) => `${a.list}/${a.itemId}`));
  const suggested: PlanAccommodation[] = [];
  for (const list of ['classroom', 'contentTests'] as const) {
    for (const item of record.catalog[list]) {
      if (ids.includes(item.id) && !present.has(`${list}/${item.id}`)) {
        suggested.push({ list, itemId: item.id, label: item.label, source: 'suggested', status: 'proposed', by: null, at: '', fromYear: '', noClassroomMatch: false });
      }
    }
  }
  return [...record.accommodations, ...suggested];
}

function accommodationInputs(items: PlanAccommodation[]): AccommodationInput[] {
  return items.map((a) => ({ list: a.list, itemId: a.itemId, on: a.status !== 'removed', source: a.source }));
}

export function IndividualPlanPage() {
  const status = usePlansStatus();
  const { schoolYear } = useParams();
  const { isSuperAdmin, isDistrictAdmin } = useAuth();
  const teacher = !isSuperAdmin && !isDistrictAdmin;
  if (status === null) {
    return null;
  }
  const laterYear = schoolYear != null && status.schoolYear != null && schoolYear >= status.schoolYear;
  if (!status.generated && !status.unknown && teacher && (!schoolYear || laterYear)) {
    return <Navigate to={NAV_ROUTES.roster} replace />;
  }
  return <IndividualPlanView teacher={teacher} />;
}

function IndividualPlanView({ teacher }: { teacher: boolean }) {
  const { studentId = '', schoolYear: yearParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const selectedYear = yearParam ?? null;
  const selectYear = (year: string | null, key: string = studentId) => navigate(planPath(key, year ?? undefined), { replace: true, state: location.state });
  const keyFor = (year: string) => record?.schoolYears.find((entry) => entry.year === year)?.studentId ?? studentId;
  const { district, hasFeature } = useAuth();
  const languageOverTime = hasFeature(FEATURE_LANGUAGE_OVER_TIME);
  const districtName = district?.name || 'Your district';
  const backState = location.state as PlansBackState;
  const count = backState?.count;
  const order = backState?.order;

  const [roster, setRoster] = useState(() => loadPlanRoster());
  const [rosterSettled, setRosterSettled] = useState(() => teacher || findInRoster(roster, studentId) != null);

  const [rosterStudentId, setRosterStudentId] = useState(studentId);
  if (studentId !== rosterStudentId) {
    setRosterStudentId(studentId);
    setRosterSettled(teacher || findInRoster(roster, studentId) != null);
  }

  useEffect(() => {
    if (teacher || findInRoster(loadPlanRoster(), studentId) != null) return;
    let cancelled = false;
    hydrateRostersFromUploads()
      .catch(() => undefined)
      .then(() => {
        if (cancelled) return;
        setRoster(loadPlanRoster());
        setRosterSettled(true);
      });
    return () => {
      cancelled = true;
    };
  }, [studentId, teacher]);

  const [record, setRecord] = useState<PlanRecord | null>(null);
  const [recordSettled, setRecordSettled] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [unreleased, setUnreleased] = useState(false);
  const [recordFailed, setRecordFailed] = useState(false);
  const recordKey = `${studentId}|${selectedYear ?? ''}`;
  const recordKeyRef = useRef(recordKey);
  useEffect(() => {
    recordKeyRef.current = recordKey;
  }, [recordKey]);
  const [loadedRecordKey, setLoadedRecordKey] = useState(recordKey);
  if (recordKey !== loadedRecordKey) {
    setLoadedRecordKey(recordKey);
    setRecord(null);
    setRecordSettled(false);
    setNotFound(false);
    setRecordFailed(false);
  }
  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    fetchPlanRecord(studentId, selectedYear ?? undefined)
      .then((r) => {
        if (cancelled) return;
        setRecord(r);
        setRecordFailed(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setRecord(null);
        if (err instanceof PlanRecordNotFoundError) setNotFound(true);
        else if (err instanceof PlanRecordForbiddenError && err.code === 'plans_not_generated') setUnreleased(true);
        else setRecordFailed(true);
      })
      .finally(() => {
        if (!cancelled) setRecordSettled(true);
      });
    return () => {
      cancelled = true;
    };
  }, [studentId, selectedYear, yearParam]);
  useEffect(() => {
    if (record?.studentId && record.studentId !== studentId) navigate(planPath(record.studentId, yearParam ?? undefined), { replace: true, state: location.state });
  }, [record, studentId, yearParam, navigate, location.state]);

  const wida = useMemo(() => findInRoster(roster, studentId), [roster, studentId]);
  const { years } = useDistrictYears();
  const districtCriteria = useDistrictExitCriteria();
  const liveStudent = useMemo(() => {
    if (!wida || !years) return null;
    const sis = findSisStudent([studentId, wida.id, wida.stateStudentId, wida.localStudentId]);
    return enrichWithSis(wida, sis, years);
  }, [wida, studentId, years]);
  const bareStudent = useMemo(() => (wida && years ? enrichWithSis(wida, null, years) : null), [wida, years]);

  const finalized = record?.version.status === 'finalized';
  const pastPlan = record != null && record.standing !== 'active';
  const historical = record != null && (pastPlan || !record.isCurrentYear);
  const futureYear = record != null && !record.isCurrentYear && (schoolYearEndYear(record.year) ?? 0) > (schoolYearEndYear(record.currentYear) ?? 0);
  const olderYear = record != null && !record.isCurrentYear && !futureYear;
  const readOnlyRecord = record != null && (finalized || !record.isCurrentYear || teacher || pastPlan);
  const body = useMemo(() => bodyOf(record), [record]);
  const bodyStudent = body?.student ?? null;
  const frozenBody = readOnlyRecord ? body : null;
  const student = frozenBody && bodyStudent ? bodyStudent : (historical ? bareStudent : liveStudent) ?? (teacher ? bodyStudent : null);
  const frozenStamp = frozenBody ? record?.version.finalizedAt || record?.content?.generatedAt || '' : '';
  const planYearEnd = historical && !futureYear ? schoolYearEndYear(record.year) : null;
  const cutStudent = useMemo(() => (student != null && planYearEnd ? throughPlanYear(student, planYearEnd) : student), [student, planYearEnd]);
  const alt = cutStudent != null && altAccess(cutStudent);
  const planStudent = useMemo(() => (cutStudent == null || alt ? cutStudent : accessYearsOnly(cutStudent)), [cutStudent, alt]);
  const rosterStudents = useMemo(() => sortByPlanName(roster), [roster]);
  const reclassCriteria = useMemo(
    () => (frozenBody?.criteria ? frozenBody.criteria : criteriaForStudent(districtCriteria.criteria, planStudent)),
    [frozenBody, districtCriteria.criteria, planStudent],
  );
  const frozenSnapshot = useMemo(() => frozenSnapshotOf(frozenBody, planStudent, reclassCriteria), [frozenBody, planStudent, reclassCriteria]);
  const [live, setLive] = useState<StudentPlanData | null>(null);
  const liveShown = historical ? null : live;
  const [liveSettled, setLiveSettled] = useState(false);
  const [benchmarksRead, setBenchmarksRead] = useState<{ key: string; data: FlashlightBenchmarksResponse | null; failed: boolean } | null>(null);

  const [suggestedIds, setSuggestedIds] = useState<string[]>([]);
  const [savedGeneration, setSavedGeneration] = useState(0);
  const [settledGenerators, setSettledGenerators] = useState<string[]>([]);
  const savedGenerationRef = useRef(0);
  const saveFailedRef = useRef(false);
  const [liveStudentId, setLiveStudentId] = useState(studentId);
  if (studentId !== liveStudentId) {
    setLiveStudentId(studentId);
    setLive(null);
    setLiveSettled(false);
    setSuggestedIds([]);
    setSavedGeneration(0);
    setSettledGenerators([]);
  }

  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    fetchStudentPlanData(studentId)
      .then((data) => {
        if (!cancelled) setLive(data);
      })
      .catch(() => {
        if (!cancelled) setLive(null);
      })
      .finally(() => {
        if (!cancelled) setLiveSettled(true);
      });
    return () => {
      cancelled = true;
    };
  }, [studentId]);

  const frozenFlashlight = record?.version.status === 'finalized' ? record.flashlight : null;
  const benchmarksAsOf = record?.version.status === 'finalized' && !frozenFlashlight ? record.version.finalizedAt : undefined;
  const hasFrozenFlashlight = frozenFlashlight != null;
  const benchmarksKey = `${studentId}|${benchmarksAsOf ?? ''}`;
  useEffect(() => {
    if (!studentId || hasFrozenFlashlight || !recordSettled) return;
    let cancelled = false;
    fetchFlashlightBenchmarks(studentId, benchmarksAsOf)
      .then((data) => {
        if (!cancelled) setBenchmarksRead({ key: benchmarksKey, data, failed: false });
      })
      .catch(() => {
        if (!cancelled) setBenchmarksRead({ key: benchmarksKey, data: null, failed: true });
      });
    return () => {
      cancelled = true;
    };
  }, [studentId, benchmarksAsOf, benchmarksKey, hasFrozenFlashlight, recordSettled]);
  const benchmarks = benchmarksRead?.key === benchmarksKey ? benchmarksRead.data : null;
  const benchmarksFailed = benchmarksRead?.key === benchmarksKey && benchmarksRead.failed;

  const liveDraft = !teacher && record != null && !readOnlyRecord;
  const hasRealPlan = !!(wida || (live && live.source === 'live') || bodyStudent);
  const collected = useRef<{
    domains: Record<string, FocusByDomainContent>;
    accommodations: string[] | null;
    suggestedItems: string[] | null;
    planSnapshot: PlanSnapshotContent | null;
    inputs: PlanGenerationInputs;
  }>({
    domains: {},
    accommodations: null,
    suggestedItems: null,
    planSnapshot: null,
    inputs: { focusByDomain: {} },
  });
  const draftKey = record != null && record.version.status === 'draft' ? `${studentId}|${record.year}|${record.version.number}` : null;
  const [liveDraftKey, setLiveDraftKey] = useState(draftKey);
  if (draftKey !== liveDraftKey) {
    setLiveDraftKey(draftKey);
    if (draftKey != null) {
      setSuggestedIds([]);
      setSavedGeneration(0);
      setSettledGenerators([]);
    }
  }
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveChain = useRef<Promise<void>>(Promise.resolve());
  const latest = useRef({ studentId, draftKey, wida, student, planStudent, criteria: reclassCriteria, live, liveSettled, hasRealPlan, liveDraft });

  useEffect(() => {
    latest.current = { studentId, draftKey, wida, student, planStudent, criteria: reclassCriteria, live, liveSettled, hasRealPlan, liveDraft };
  });

  useEffect(() => {
    if (draftKey == null) return;
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    savedGenerationRef.current = 0;
    saveFailedRef.current = false;
    collected.current = { domains: {}, accommodations: null, suggestedItems: null, planSnapshot: null, inputs: { focusByDomain: {} } };
  }, [draftKey]);

  useEffect(() => {
    collected.current = { domains: {}, accommodations: null, suggestedItems: null, planSnapshot: null, inputs: { focusByDomain: {} } };
    savedGenerationRef.current = 0;
    saveFailedRef.current = false;
    resetPlanCache();
  }, [studentId]);

  const runSave = useCallback(() => {
    const d = latest.current;
    if (!d.studentId || !d.liveSettled || !d.hasRealPlan || !d.liveDraft) return;
    const liveName = hasLiveValue(d.live?.student.firstName) && hasLiveValue(d.live?.student.lastName)
      ? `${d.live.student.firstName} ${d.live.student.lastName}`
      : '';
    const forStudent = d.studentId;
    const forDraft = d.draftKey;
    const snapshot = buildPlanSnapshot({
      studentId: forStudent,
      fullName: d.wida?.fullName || liveName || 'Student',
      school: schoolName(d.student, d.live),
      grade: gradeText(d.student, d.live),
      elTeacher: elTeacherName(d.student, d.live),
      demographics: d.live?.student ?? null,
      focusByDomain: collected.current.domains,
      accommodations: collected.current.accommodations,
      suggestedItems: collected.current.suggestedItems,
      planSnapshot: collected.current.planSnapshot,
      inputs: collected.current.inputs,
      student: d.planStudent,
      criteria: d.criteria,
    });
    saveChain.current = saveChain.current.then(() =>
      saveStudentPlan(forStudent, snapshot).then(
        ({ version }) => {
          if (latest.current.studentId === forStudent && latest.current.draftKey === forDraft) {
            saveFailedRef.current = false;
            savedGenerationRef.current = Math.max(savedGenerationRef.current, version);
            setSavedGeneration((v) => Math.max(v, version));
          }
        },
        () => {
          if (latest.current.studentId === forStudent && latest.current.draftKey === forDraft) saveFailedRef.current = true;
        },
      ),
    );
  }, []);

  const flushSave = useCallback(async () => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
      runSave();
    }
    await saveChain.current;
    if (saveFailedRef.current) throw new Error('generation save failed');
  }, [runSave]);

  const scheduleSave = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveTimer.current = null;
      runSave();
    }, 1500);
  }, [runSave]);

  const handleDomainContent = useCallback(
    (domain: string, content: FocusByDomainContent, inputs: FocusByDomainInputs) => {
      collected.current.domains[domain] = content;
      collected.current.inputs.focusByDomain = {
        ...collected.current.inputs.focusByDomain,
        [domain]: inputs,
      };
      scheduleSave();
    },
    [scheduleSave],
  );
  const handleAccommodations = useCallback((items: string[], inputs: AccommodationInputs) => {
    collected.current.accommodations = items;
    collected.current.inputs.accommodations = inputs;
    scheduleSave();
  }, [scheduleSave]);
  const handleSettled = useCallback((generator: string) => {
    setSettledGenerators((done) => (done.includes(generator) ? done : [...done, generator]));
  }, []);
  const handleSuggestedItems = useCallback((ids: string[]) => {
    collected.current.suggestedItems = ids;
    setSuggestedIds(ids);
    scheduleSave();
  }, [scheduleSave]);
  const handlePlanSnapshot = useCallback(
    (content: PlanSnapshotContent, inputs: PlanSnapshotInputs) => {
      collected.current.planSnapshot = content;
      collected.current.inputs.planSnapshot = inputs;
      scheduleSave();
    },
    [scheduleSave],
  );

  useEffect(() => {
    const hasContent =
      Object.keys(collected.current.domains).length > 0 ||
      collected.current.accommodations !== null ||
      collected.current.planSnapshot !== null;
    if (liveSettled && hasRealPlan && liveDraft && (hasContent || alt)) scheduleSave();
  }, [studentId, liveSettled, hasRealPlan, liveDraft, alt, scheduleSave]);

  useEffect(() => () => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
      runSave();
    }
  }, [studentId, runSave]);

  const [snapshotEditing, setSnapshotEditing] = useState(false);
  const [accEditing, setAccEditing] = useState(false);
  const [editorEpoch, setEditorEpoch] = useState(0);
  const [editingKey, setEditingKey] = useState(recordKey);
  if (recordKey !== editingKey) {
    setEditingKey(recordKey);
    setSnapshotEditing(false);
    setAccEditing(false);
  }
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; next: OrderEntry | null } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [noteRequest, setNoteRequest] = useState(0);

  const goBackToPlans = () => {
    if (teacher) {
      navigate(NAV_ROUTES.roster, { state: APP_LINK_STATE });
      return;
    }
    navigate('/plans', count != null ? { state: { count, skipLoad: true } } : undefined);
  };

  const goToStudent = (id: string) => {
    navigate(planPath(id), { state: { ...(count != null ? { count } : {}), ...(order ? { order } : {}) } });
  };
  const goToProfile = () => navigate(`/roster/${encodeURIComponent(studentId)}`);

  const rosterEntries: RosterEntry[] = rosterStudents.map((s) => ({ id: s.id, name: s.fullName }));
  const rosterIndex = rosterStudents.findIndex(
    (s) => s.id === studentId || s.stateStudentId === studentId || s.localStudentId === studentId,
  );
  const orderEntries: OrderEntry[] = order
    ? order.map((id) => ({ id, name: rosterStudents.find((s) => s.id === id || s.stateStudentId === id || s.localStudentId === id)?.fullName ?? id }))
    : rosterEntries;

  const liveName = hasLiveValue(live?.student.firstName) && hasLiveValue(live?.student.lastName)
    ? `${live.student.firstName} ${live.student.lastName}`
    : '';
  const fullName = student?.fullName || wida?.fullName || liveName || body?.fullName || 'Student';
  const firstName = student?.firstName || fullName.split(' ')[0] || 'This student';

  const headerStudent = historical ? planStudent : student;
  const school = schoolName(headerStudent, liveShown);
  const crumbs: Crumb[] = record && pastPlan
    ? [
      { label: 'Students', onClick: () => navigate(NAV_ROUTES.roster, { state: APP_LINK_STATE }) },
      { label: fullName, onClick: goToProfile },
      { label: `${displayYear(record.year)} plan` },
    ]
    : [
      { label: teacher ? 'Students' : 'Plans', onClick: goBackToPlans },
      ...(school ? [{ label: school, onClick: goBackToPlans }] : []),
      { label: fullName },
    ];

  const topBar = (
    <TopBar
      crumbs={crumbs}
      index={pastPlan ? -1 : rosterIndex}
      total={pastPlan ? 0 : rosterEntries.length}
      roster={rosterEntries}
      onSelect={goToStudent}
      onPrev={() => {
        const prev = rosterEntries[rosterIndex - 1];
        if (prev) goToStudent(prev.id);
      }}
      onNext={() => {
        const next = rosterEntries[rosterIndex + 1];
        if (next) goToStudent(next.id);
      }}
    />
  );

  const revisionRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    revisionRef.current = record?.version.revision;
  }, [record]);
  const applyRecord = (next: PlanRecord, forKey: string = recordKey) => {
    if (!next || forKey !== recordKeyRef.current) return;
    setRecord((current) => {
      if (olderThanShown(next, current)) return current;
      revisionRef.current = next.version.revision;
      return next;
    });
  };
  const withNotice = async (work: () => Promise<PlanRecord>, fallback: string) => {
    const forKey = recordKey;
    try {
      applyRecord(await work(), forKey);
    } catch (err) {
      if (forKey !== recordKeyRef.current) throw err;
      if (err instanceof PlanRecordConflictError && err.code === 'stale_generation') {
        setNotice('This plan was regenerated since you opened it. Review the new draft before finalizing.');
        fetchPlanRecord(studentId, selectedYear ?? undefined).then((r) => applyRecord(r, forKey)).catch(() => undefined);
      } else if (err instanceof PlanRecordConflictError && err.code === 'plan_changed') {
        setNotice('This plan changed in another tab or by another admin. It has been reloaded; review it and try again.');
        setSnapshotEditing(false);
        setAccEditing(false);
        setEditorEpoch((epoch) => epoch + 1);
        fetchPlanRecord(studentId, selectedYear ?? undefined).then((r) => applyRecord(r, forKey)).catch(() => undefined);
      } else {
        setNotice(fallback);
      }
      throw err;
    }
  };

  const accommodationsShown = record ? suggestedAsAccommodations(record, suggestedIds) : [];
  const year = record?.year ?? '';

  const confirmFinalize = async () => {
    if (!record) return;
    setFinalizeOpen(false);
    const forKey = recordKey;
    try {
      let current = record;
      if (!current.accommodationsSaved && !current.alternateAccess) {
        current = await savePlanAccommodations(studentId, year, accommodationInputs(accommodationsShown), record.version.revision);
        applyRecord(current, forKey);
      }
      try {
        await flushSave();
      } catch {
        setNotice('Couldn’t save the generated plan. Try again.');
        return;
      }
      const generationVersion = Math.max(record.version.generationVersion, savedGenerationRef.current, savedGeneration);
      const reviewedRevision = current.version.revision;
      await withNotice(() => finalizePlan(studentId, year, generationVersion, reviewedRevision), 'Couldn’t finalize the plan. Try again.');
      setToast({ message: finalizedToastMessage(firstName, year), next: nextStudentAfter(orderEntries, studentId) });
    } catch {
      return;
    }
  };

  const onRevise = () => {
    withNotice(() => revisePlan(studentId, year), 'Couldn’t start a revision. Try again.').catch(() => undefined);
  };
  const onSignOff = () => {
    if (!record) return;
    withNotice(() => signOffPlan(studentId, year, record.version.number), 'Couldn’t record your sign-off. Try again.').catch(() => undefined);
  };
  const onSaveSnapshot = async (input: SnapshotSaveInput) => {
    try {
      await flushSave();
    } catch {
      setNotice('Couldn’t save the generated plan. Try again.');
      throw new Error('generation_unsaved');
    }
    await withNotice(() => savePlanSnapshot(studentId, year, input, record?.version.revision), 'Couldn’t save the Plan Snapshot. Try again.');
  };
  const onSaveAccommodations = (items: AccommodationInput[]) =>
    withNotice(() => savePlanAccommodations(studentId, year, items, revisionRef.current), 'Couldn’t save the accommodations. Try again.').then(() => undefined);
  const onAddNote = (text: string) =>
    withNotice(() => addPlanNote(studentId, year, text), 'Couldn’t save the note. Try again.').then(() => undefined);

  if (unreleased) {
    return <Navigate to={NAV_ROUTES.roster} replace />;
  }
  if (recordFailed) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography sx={{ fontSize: 15 }}>We could not load this plan. Nothing has been lost. Refresh the page to try again.</Typography>
        <Button onClick={goBackToPlans} startIcon={<ArrowBackIcon />} sx={{ mt: 2, textTransform: 'none', color: COLORS.navy }}>
          {teacher ? 'Back to Students' : 'Back to Plans'}
        </Button>
      </Box>
    );
  }
  if (notFound) {
    return <Navigate to={`/roster/${encodeURIComponent(studentId)}`} replace />;
  }

  if ((!wida && !bodyStudent && (!rosterSettled || !liveSettled)) || years == null || !districtCriteria.settled || !recordSettled) {
    return (
      <PageShell topBar={topBar}>
        <PlanHeader fullName="Student Action Plan" />
        <PlanCard>
          <Typography sx={{ fontSize: 16, color: COLORS.muted }}>
            Loading this student&rsquo;s plan&hellip;
          </Typography>
        </PlanCard>
      </PageShell>
    );
  }

  const notesCard = record ? (
    <NotesCard
      firstName={firstName}
      notes={record.notes}
      onAdd={onAddNote}
      openRequest={noteRequest}
      canAdd={!record.notesReadOnly}
      subtitle={notesSubtitle(firstName, districtName, record.standing)}
    />
  ) : null;

  if (teacher && record && !record.content) {
    return (
      <PageShell topBar={topBar}>
        <PlanHeader fullName={fullName} chip={{ label: 'Draft Plan • Awaiting Review', tone: 'draft' }} showShare={false} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pb: 4 }}>
          <PlanCard title="This plan hasn't been drafted yet">
            <Typography sx={{ fontSize: 16, fontWeight: 500, lineHeight: '24px', color: COLORS.muted }}>
              {`${districtName} hasn't generated ${firstName}'s ${displayYear(year)} plan yet. You'll be asked to review it once it's finalized. You can add notes now.`}
            </Typography>
          </PlanCard>
          {notesCard}
        </Box>
      </PageShell>
    );
  }

  const legacyBody = (teacher || pastPlan) && frozenBody != null && frozenBody.planSnapshot != null && planStudent == null;
  if (!wida && !(live && live.source === 'live') && !bodyStudent && !legacyBody) {
    return (
      <PageShell topBar={topBar}>
        <PlanHeader fullName="Student Action Plan" />
        <PlanCard>
          <Typography sx={{ fontSize: 16, color: COLORS.ink }}>
            We couldn&rsquo;t find a plan for that student.
          </Typography>
          <Button
            onClick={goBackToPlans}
            startIcon={<ArrowBackIcon />}
            sx={{ mt: 1, textTransform: 'none', color: COLORS.navy, alignSelf: 'flex-start' }}
          >
            {teacher ? 'Back to Students' : 'Back to Plans'}
          </Button>
        </PlanCard>
      </PageShell>
    );
  }

  const items = metaItems(planStudent, liveShown, historical ? null : record?.iep, historical ? record.standing : null);
  const sisMissing = !historical && sisFieldsMissing(student, live);
  const latestRecord = planStudent ? planStudent.years[planStudent.years.length - 1] : undefined;
  const latestYear = latestRecord ? latestRecord.year : null;
  const liveBenchmarksUsable = benchmarks != null && !benchmarksFailed && benchmarks.rubricUncertain !== true && !benchmarks.sources.some((source) => source.status === 'error' || source.status === 'skipped') ? benchmarks : null;
  const flashlight = getFlashlightData(
    frozenFlashlight ? (frozenFlashlight.status === 'saved' ? { hasData: frozenFlashlight.years.length > 0, years: frozenFlashlight.years, sources: [] } : null) : liveBenchmarksUsable,
    latestYear,
  );
  const planYear = record?.year ?? `${calendarSchoolYearEnd() - 1}-${String(calendarSchoolYearEnd()).slice(2)}`;
  const benchmarksSource: PlanBenchmarksSource = frozenFlashlight
    ? { kind: 'frozen', data: frozenFlashlight }
    : benchmarksAsOf
      ? { kind: 'asOf', asOf: benchmarksAsOf, data: benchmarks, failed: benchmarksFailed }
      : { kind: 'live', data: benchmarks, failed: benchmarksFailed };
  const teacherName = elTeacherName(headerStudent, liveShown);

  const admin = !teacher;
  const pastYear = record != null && !record.isCurrentYear;
  const expectedGenerators = alt ? [] : ['planSnapshot', 'accommodations', 'listening', 'speaking', 'reading', 'writing'];
  const generatorsSettled = !liveDraft || expectedGenerators.every((g) => settledGenerators.includes(g));
  const accommodationsSettled = !liveDraft || alt || settledGenerators.includes('accommodations');
  const canFinalize = record != null && generatorsSettled && record.canEdit && savedGeneration > 0;
  let chip: { label: string; tone: 'draft' | 'final' | 'grey' } = { label: 'Draft Plan • Awaiting Review', tone: 'draft' };
  let when = `Generated ${shortDate(new Date())}`;
  if (record && (pastPlan || olderYear) && !finalized) {
    chip = { label: 'Generated', tone: 'grey' };
    when = `Generated from ${latestYear ? `Feb ${latestYear} ` : ''}ACCESS`;
  } else if (record && finalized) {
    chip = { label: `Finalized ${formatPlanDate(record.version.finalizedAt)}`, tone: 'final' };
    when = `Finalized ${formatPlanDate(record.version.finalizedAt)} by ${record.version.finalizedBy?.name ?? districtName}`;
  } else if (record && record.version.draftSource === 'revised') {
    chip = { label: 'Draft Plan • Revising', tone: 'draft' };
    when = `Revising the plan finalized ${formatPlanDate(record.version.revisedFrom)} · saves as a new version`;
  } else if (record && record.version.draftedAt) {
    when = `Drafted ${formatPlanMonthDay(record.version.draftedAt)} from Spring ${latestYear ?? ''} ${alt ? 'Alternate ' : ''}ACCESS`.replace('  ', ' ');
  }
  const subline = [school, gradeText(headerStudent, liveShown), teacherName ? `EL Teacher: ${teacherName}` : '', when]
    .filter(Boolean)
    .join(' · ');
  const yearMenu: PlanYearMenu | undefined = record
    ? { years: record.schoolYears, selected: record.year, onSelect: (y) => selectYear(!pastPlan && y === record.currentYear ? null : y, keyFor(y)) }
    : undefined;
  const actions = record && admin && !pastYear && !pastPlan ? (
    finalized ? (
      <Button variant="outlined" onClick={onRevise} sx={{ textTransform: 'none', fontWeight: 600, color: COLORS.navy, borderColor: COLORS.hairline, px: 2 }}>
        Revise plan
      </Button>
    ) : accEditing ? null : (
      <Button
        variant="contained"
        disabled={snapshotEditing || !canFinalize}
        onClick={() => setFinalizeOpen(true)}
        sx={{ textTransform: 'none', fontWeight: 700, bgcolor: COLORS.navy, px: 2, '&:hover': { bgcolor: '#23236F' } }}
      >
        Finalize plan
      </Button>
    )
  ) : null;
  const showShare = record ? finalized && admin && !pastYear && !pastPlan : true;

  const topSection = record
    ? pastYear || pastPlan
      ? null
      : teacher
        ? finalized
          ? (record.canSignOff || record.mySignOff)
            ? (
              <SignOffBar
                firstName={firstName}
                districtName={districtName}
                year={record.year}
                finalizedAt={record.version.finalizedAt}
                finalizedBy={record.version.finalizedBy?.name ?? districtName}
                currentVersion={record.version.number}
                revisedWhat={record.version.revisedWhat}
                reviewed={record.expectedReviewers.filter((r) => r.status === 'reviewed').length}
                reviewers={record.expectedReviewers.length}
                mySignOff={record.mySignOff}
                onSignOff={onSignOff}
                onAddNote={() => setNoteRequest((n) => n + 1)}
              />
            )
            : null
          : <DraftBanner firstName={firstName} districtName={districtName} year={record.year} />
        : finalized
          ? (
            <SignOffsCard
              firstName={firstName}
              districtName={districtName}
              year={record.year}
              rule={record.signOffRule}
              rosteringKnown={record.rosteringKnown}
              reviewers={record.expectedReviewers}
              alsoReviewed={record.alsoReviewed}
              onAddReviewer={() => setNotice('Adding a reviewer by hand isn’t built yet. Rostering decides who is asked.')}
            />
          )
          : null
    : null;

  const accommodationsCard = record ? (
    <AccommodationsCard
      key={`accommodations-${editorEpoch}`}
      firstName={firstName}
      districtName={districtName}
      year={record.year}
      status={finalized ? 'finalized' : 'draft'}
      finalizedAt={record.version.finalizedAt}
      items={olderYear && !finalized && !record.accommodationsSaved ? [] : accommodationsShown}
      pastPlan={olderYear ? { currentYear: record.currentYear, saved: record.accommodationsSaved } : null}
      iep={historical ? { sis: null, section504: null, access: null } : record.iep}
      alternateAccess={historical ? alt : record.alternateAccess}
      canEdit={record.canEdit && !snapshotEditing && accommodationsSettled}
      catalog={record.catalog}
      onSave={onSaveAccommodations}
      onEditingChange={setAccEditing}
    />
  ) : null;

  const recordOverlays = (
    <>
      {finalizeOpen && record && (
        <FinalizeDialog open firstName={firstName} year={record.year} prevYear={previousYear(record.year)} onCancel={() => setFinalizeOpen(false)} onConfirm={() => { void confirmFinalize(); }} />
      )}
      {toast && (
        <FinalizedToast
          message={toast.message}
          next={toast.next}
          onNext={(id) => {
            setToast(null);
            goToStudent(id);
          }}
          onClose={() => setToast(null)}
        />
      )}
      {notice && (
        <FinalizedToast message={notice} next={null} onNext={() => undefined} onClose={() => setNotice(null)} />
      )}
    </>
  );

  return (
    <PageShell topBar={topBar}>
      {record && futureYear ? (
        <InfoBanner message={`You're viewing ${firstName}'s ${displayYear(record.year)} plan. It isn't this year's plan, so it can't be changed here.`} actionLabel={pastPlan ? `View ${firstName}'s profile` : `View ${displayYear(record.currentYear)} plan`} onAction={pastPlan ? goToProfile : () => selectYear(null, keyFor(record.currentYear))} />
      ) : record && pastPlan ? (
        <InfoBanner message={pastPlanMessage(firstName, record.year, record.standing, finalized)} actionLabel={`View ${firstName}'s profile`} onAction={goToProfile} />
      ) : record && pastYear && (
        <InfoBanner message={pastYearMessage(firstName, record.year, finalized)} actionLabel={`View ${displayYear(record.currentYear)} plan`} onAction={() => selectYear(null, keyFor(record.currentYear))} />
      )}
      {alt && student ? (
        <>
          <AltHeader
            data={buildAltHeader(planStudent ?? student, liveShown, pastPlan ? record.standing : null)}
            generatedDate={fullDate(frozenStamp ? new Date(frozenStamp) : new Date())}
          />
          {record && (yearMenu || pastPlan) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', py: 1.5 }}>
              {yearMenu ? <YearMenu menu={yearMenu} /> : <YearPill label={record.year} />}
              <PlanChip label={chip.label} tone={chip.tone} />
              <Typography sx={{ fontSize: 14, fontWeight: 500, color: COLORS.muted, flex: 1 }}>{when}</Typography>
              {actions}
            </Box>
          )}
        </>
      ) : (
        <>
          <PlanHeader
            fullName={fullName}
            chip={chip}
            yearMenu={yearMenu}
            yearLabel={pastPlan ? record.year : undefined}
            subline={subline}
            actions={actions}
            showShare={showShare}
          />
          <Box sx={{ borderTop: `1px solid ${COLORS.hairline}` }} />
          <MetaStrip items={items} sisMissing={sisMissing} onUploadSis={admin ? () => navigate(NAV_ROUTES.data) : undefined} />
        </>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pb: 4, pt: alt ? 2.5 : 0 }}>
        {topSection}
        {planStudent != null && alt ? (
          <>
            <AltPlanSnapshot student={planStudent ?? student} pastPlan={olderYear || pastPlan} />
            {accommodationsCard}
            {notesCard}
            <AltFocusByDomain student={planStudent} />
            <AltProficiencyPanel student={planStudent ?? student} />
            <AltTrajectory student={planStudent ?? student} />
            <AltDomainProfile student={planStudent} />
            {languageOverTime && <FlashlightBenchmarksCard planYear={planYear} source={benchmarksSource} onOpenProfile={goToProfile} subtitle="Within-year progress on the 1–5 Flashlight rubric · a separate measure from Alternate ACCESS, unaffected by it" />}
            <StateAssessments />
          </>
        ) : planStudent != null || legacyBody ? (
          <ReclassCriteriaProvider value={reclassCriteria}>
            <PlanSnapshot
              key={`${studentId}-${record?.year ?? ''}-${frozenBody ? 'frozen' : 'live'}-${editorEpoch}`}
              student={planStudent}
              studentId={studentId}
              year={latestYear}
              onAccommodations={liveDraft ? handleAccommodations : undefined}
              onPlanSnapshot={liveDraft ? handlePlanSnapshot : undefined}
              onSuggestedItems={liveDraft ? handleSuggestedItems : undefined}
              frozen={frozenSnapshot}
              record={record ? { snapshotEdits: record.snapshotEdits, supportsRemoved: record.supportsRemoved, supportsAdded: record.supportsAdded } : null}
              canEdit={!!record?.canEdit && !accEditing}
              onSaveEdits={record?.canEdit ? onSaveSnapshot : undefined}
              onEditingChange={setSnapshotEditing}
              catalog={record?.catalog ?? null}
              alternateAccess={record?.alternateAccess ?? false}
              onSettled={liveDraft ? handleSettled : undefined}
              pastPlan={olderYear || pastPlan}
            />
            {accommodationsCard}
            {notesCard}
            {planStudent != null ? (
              <>
                <FocusByDomain student={planStudent} studentId={studentId} onContent={liveDraft ? handleDomainContent : undefined} frozen={frozenBody ? frozenBody.focusByDomain ?? {} : null} onSettled={liveDraft ? handleSettled : undefined} />
                <ProficiencyCard student={planStudent} year={latestYear} />
                <CompositeTrajectory student={planStudent} />
                <DomainProfile student={planStudent} flashlight={flashlight} />
              </>
            ) : (
              <PlanCard title="Focus by Domain, proficiency and trajectory" subtitle="Driven by the WIDA ACCESS history saved with the plan">
                <Typography sx={{ fontSize: 14, fontWeight: 500, lineHeight: 1.6, color: COLORS.muted }}>
                  {`This draft was generated before we began saving ${firstName}'s ACCESS history with the plan. These sections appear once ${districtName} next opens or finalizes it.`}
                </Typography>
              </PlanCard>
            )}
            {languageOverTime && <FlashlightBenchmarksCard planYear={planYear} source={benchmarksSource} onOpenProfile={goToProfile} />}
            <StateAssessments />
          </ReclassCriteriaProvider>
        ) : (
          <PlanCard title="WIDA ACCESS history" subtitle="Proficiency, trajectory, and domain analysis">
            <Typography sx={{ fontSize: 14, fontWeight: 500, lineHeight: 1.6, color: COLORS.muted }}>
              No WIDA ACCESS history is connected for this student yet. Upload a WIDA ACCESS history export
              (one row per administration, with per-domain scale scores and proficiency levels) to populate the
              proficiency snapshot, exit status, composite trajectory, and per-domain analysis.
            </Typography>
          </PlanCard>
        )}

        <Box sx={{ px: 0.5 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 500, fontStyle: 'italic', color: COLORS.muted, lineHeight: 1.5 }}>
            {frozenBody ? `Plan frozen ${formatPlanDate(record?.version.finalizedAt || record?.content?.generatedAt || '')}. ` : `Plan generated ${shortDate(new Date())}. `}Data sections are driven by the uploaded WIDA{alt ? ' Alternate' : ''} ACCESS history{student?.sis ? ' and SIS roster' : ''};
            LTEL threshold: {years.ltelMinYears}+ years in EL treated as Long-Term EL.
            {alt
              ? 'Narrative summaries are derived from the score data; instructional recommendations and supports are deferred for Alternate ACCESS — IEP / case-conference team decisions.'
              : 'Narrative summaries are derived from the score data; instructional recommendations come from the LLM layer.'}
          </Typography>
        </Box>
      </Box>
      {recordOverlays}
    </PageShell>
  );
}
